import { useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import "bootstrap/dist/css/bootstrap.min.css";

import SalaryTable from "./SalaryTable";
import SalaryForm from "./SalaryForm";
import SalaryHistoryModal from "./SalaryHistoryModal";
import SalaryImportModal from "./SalaryImportModal";

import {
  createSalaryRevision,
  getActiveSalaries,
  getSalaryHistory,
} from "../../services/hr/salaryService";
import { getEmployees } from "../../services/hr/employeeService";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.error || error?.response?.data?.message || fallback;

const Salary = () => {
  const [salaries, setSalaries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  const [employees, setEmployees] = useState([]);

  const [formMode, setFormMode] = useState(null); // "initial" | "increment" | "adjustment"
  const [activeSalary, setActiveSalary] = useState(null);

  const [importType, setImportType] = useState(null); // "salaries" | "increments"

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyEmployee, setHistoryEmployee] = useState(null);
  const [historySalaries, setHistorySalaries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchSalaries = useCallback(
    async (pageOverride, limitOverride) => {
      const activePage = pageOverride ?? page;
      const activeLimit = limitOverride ?? limit;

      setLoading(true);
      try {
        const response = await getActiveSalaries(activePage, activeLimit);
        setSalaries(response.salaries || []);
        setTotal(response.total || 0);
      } catch (error) {
        console.error("Failed to fetch salaries:", error);
        toast.error(getErrorMessage(error, "Failed to load salary records"));
      } finally {
        setLoading(false);
      }
    },
    [page, limit]
  );

  useEffect(() => {
    fetchSalaries();
  }, [fetchSalaries]);

  const fetchEmployeesForSelect = useCallback(async () => {
    try {
      const response = await getEmployees(1, 1000);
      setEmployees(response.employees || []);
    } catch (error) {
      console.error("Failed to fetch employees:", error);
      toast.error("Failed to load employee list");
    }
  }, []);

  const handleOpenInitialForm = () => {
    if (employees.length === 0) {
      fetchEmployeesForSelect();
    }
    setActiveSalary(null);
    setFormMode("initial");
  };

  const handleIncrement = (salary) => {
    setActiveSalary(salary);
    setFormMode("increment");
  };

  const handleAdjustment = (salary) => {
    setActiveSalary(salary);
    setFormMode("adjustment");
  };

  const closeForm = () => {
    setFormMode(null);
    setActiveSalary(null);
  };

  const handleFormSubmit = async (payload) => {
    try {
      const result = await createSalaryRevision(payload);
      toast.success(result.message || "Salary record created successfully");
      closeForm();
      setPage(1);
      await fetchSalaries(1, limit);
    } catch (error) {
      console.error("Failed to save salary record:", error);
      toast.error(getErrorMessage(error, "Failed to save salary record"));
    }
  };

  const handleHistory = async (salary) => {
    setHistoryOpen(true);
    setHistoryEmployee(salary);
    setHistorySalaries([]);
    setHistoryLoading(true);

    try {
      const response = await getSalaryHistory(salary.employee_id);
      setHistorySalaries(response.salaries || []);
    } catch (error) {
      console.error("Failed to fetch salary history:", error);
      toast.error(getErrorMessage(error, "Failed to load salary history"));
      setHistoryOpen(false);
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistory = () => {
    setHistoryOpen(false);
    setHistoryEmployee(null);
    setHistorySalaries([]);
  };

  const handlePageChange = (newPage) => setPage(newPage);
  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };
  const handleSearchChange = (newSearch) => setSearchTerm(newSearch);

  return (
    <div className="container-fluid mt-4">
      <div className="row justify-content-center">
        <div className="col-md-12">
          <div className="d-flex justify-content-end gap-2 mt-4 flex-wrap">
            <button
              className="btn btn-outline-success"
              onClick={() => setImportType("salaries")}
            >
              Import Salaries
            </button>
            <button
              className="btn btn-outline-success"
              onClick={() => setImportType("increments")}
            >
              Import Increments
            </button>
            <button className="btn btn-primary" onClick={handleOpenInitialForm}>
              + Add Initial Salary
            </button>
          </div>

          <SalaryTable
            salaries={salaries}
            total={total}
            currentPage={page}
            itemsPerPage={limit}
            loading={loading}
            searchTerm={searchTerm}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            onSearch={handleSearchChange}
            onHistory={handleHistory}
            onIncrement={handleIncrement}
            onAdjustment={handleAdjustment}
          />

          {formMode && (
            <SalaryForm
              mode={formMode}
              employees={employees}
              activeSalary={activeSalary}
              onSubmit={handleFormSubmit}
              close={closeForm}
            />
          )}

          {importType && (
            <SalaryImportModal
              importType={importType}
              close={() => setImportType(null)}
              onImportComplete={() => fetchSalaries(1, limit)}
            />
          )}

          {historyOpen && (
            <SalaryHistoryModal
              loading={historyLoading}
              employee={historyEmployee}
              salaries={historySalaries}
              close={closeHistory}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Salary;
